import { signTypedData } from '@wagmi/core';
import { Hex } from 'viem';
import { privateKeyToAccount as _privateKeyToAccount, Address } from 'viem/accounts';
import { Config } from 'wagmi';
import { SignatureType } from '../../constants';
import { SchemaDomainTypeDto } from '../../generated/apiTypes';
import { getSpecificSignatureType, SignTypedDataTypes } from '../../hooks/useDataForSigning';
import { fetchClient as _fetchClient, dataOrThrow, FetchClient } from '../../lib/apiClient';
import { Gwei } from '../../lib/gwei';
import { getNonceInNanoseconds, nowInSeconds } from '../../lib/time';
import { PrettyMerge } from '../../lib/tsUtilityTypes';
import { InitiateWithdrawMessage } from '../../types/generated';

export type InitiateWithdrawSignatureParams = {
  subaccountName: Hex;
  account: Address;
  partialInitiateWithdrawData: { tokenId: string; token: Address; amount: bigint };
  domain: SchemaDomainTypeDto;
  signedTypedDataTypes: SignTypedDataTypes;
  config: Config;
  deps?: { fetchClient: FetchClient; privateKeyToAccount: typeof _privateKeyToAccount };
};

export const initiateWithdrawSignature = async ({
  account,
  subaccountName,
  partialInitiateWithdrawData,
  config,
  signedTypedDataTypes,
  domain,
}: InitiateWithdrawSignatureParams) => {
  const nonce = getNonceInNanoseconds();
  const signedAt = nowInSeconds();
  const message: Omit<InitiateWithdrawMessage, 'signature'> = {
    account: account,
    subaccount: subaccountName,
    token: partialInitiateWithdrawData.token,
    amount: partialInitiateWithdrawData.amount,
    nonce,
    signedAt: BigInt(signedAt),
  };

  const signature = await signTypedData(config, {
    domain,
    types: getSpecificSignatureType(signedTypedDataTypes, SignatureType.INITIATE_WITHDRAW),
    primaryType: SignatureType.INITIATE_WITHDRAW,
    message,
  });

  return { signature, message };
};

type InitiateWithdrawSignatureResponse = Awaited<ReturnType<typeof initiateWithdrawSignature>>;
export type InitiateWithdrawParams = PrettyMerge<
  InitiateWithdrawSignatureResponse,
  { tokenId: string }
>;

export const initiateWithdraw = async (
  { signature, message, tokenId }: InitiateWithdrawParams,
  deps: { fetchClient: FetchClient } = { fetchClient: _fetchClient }
) => {
  return deps.fetchClient
    .POST('/v1/token/{id}/withdraw', {
      params: { path: { id: tokenId } },
      body: {
        signature,
        data: {
          account: message.account,
          subaccount: message.subaccount,
          token: message.token,
          amount: Gwei.fmt(message.amount),
          nonce: message.nonce.toString(),
          signedAt: Number(message.signedAt),
        },
      },
    })
    .then(dataOrThrow);
};
